import React from 'react';
import { useTranslation } from 'react-i18next';
import { groupBy } from 'lodash';
import { TooltipProps } from 'recharts';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import { AggregateVectorParameterTypeInternal } from '../../../../../../../../../data-access/gql-types/graphql';
import { aggregatedParameters } from '../../../../../measurements/utils';
import { ChartDataValues, CostEnum } from '../../../types';
import { costParameters, formatMeasurementWithUnit } from '../../../utils/helpers';
import './index.scss';

type CustomTooltipProps = TooltipProps<ValueType, NameType> & {
  itemPrefix: string;
};

export const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, itemPrefix }) => {
  const { t } = useTranslation('channel-details');
  const { t: tc } = useTranslation('common');

  const formatValue = (el: ChartDataValues, isPhase: boolean) => {
    const paramConfig = aggregatedParameters[el.type];
    if (!paramConfig) return null;

    if (el.type === AggregateVectorParameterTypeInternal.Price && isPhase) {
      return null;
    }

    const formattedValue = formatMeasurementWithUnit(el.value, paramConfig);

    return el.type === AggregateVectorParameterTypeInternal.Price ? `${formattedValue} / kWh` : formattedValue;
  };

  const getLabel = (el: ChartDataValues, isPhase: boolean) => {
    if (el.type === AggregateVectorParameterTypeInternal.Price && !isPhase) {
      return (
        <span style={{ color: el.color }}>{`${t(
          `energyMeasurements.types.summary.${AggregateVectorParameterTypeInternal.Price}`,
        )}: `}</span>
      );
    }

    if (costParameters.includes(el.type)) {
      const predictedCost = el.data && 'predictedCost' in el.data;

      return (
        <span style={{ color: el.color }}>{`${t(
          `energyMeasurements.types.summary.${!predictedCost ? el.type : CostEnum.PredictedCost}`,
        )}: `}</span>
      );
    }

    return <span style={{ color: el.color }}>{`${t(`energyMeasurements.types.summary.${el.type}`)}: `}</span>;
  };

  if (active && payload?.length) {
    const values = payload[0].payload.values;

    return (
      <div className="custom-tooltip">
        {payload[0].payload?.tooltipTime && (
          <div style={{ textAlign: 'center', width: '100%' }}>{payload[0].payload.tooltipTime}</div>
        )}
        {Object.entries(groupBy(values, 'index')).map(([key, group]) => {
          const isPhase = key !== '0';

          return (
            <div key={key} className="m-b-8">
              {isPhase && <span>{`${itemPrefix} ${key}`}</span>}
              {group.map((el) => (
                <div key={el.type} className="tooltip__element">
                  <span style={{ color: el.color }}>{getLabel(el, isPhase)}</span>
                  <span>{!el.empty ? formatValue(el, isPhase) : tc('noData')}</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  return null;
};
